import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { db } from '../lib/firebase';
import {
  collection, query, orderBy, onSnapshot,
  doc, addDoc, updateDoc, deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

/**
 * Store pour la collection `blocks` (composants réutilisables d'une recette).
 *
 * onSnapshot live, pas de pagination (volume modéré attendu : quelques
 * centaines de blocs au maximum).
 */
export const useBlocksStore = defineStore('blocks', () => {
  const blocks = ref([]);
  const loading = ref(false);
  let unsub = null;

  async function fetchBlocks() {
    if (!db || unsub) return;
    loading.value = true;
    const q = query(collection(db, 'blocks'), orderBy('updatedAt', 'desc'));
    unsub = onSnapshot(
      q,
      (snap) => {
        // id en DERNIER pour qu'un champ `id` parasite dans data() ne l'écrase pas
        blocks.value = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
        loading.value = false;
      },
      (err) => {
        console.error('[blocksStore] onSnapshot error:', err);
        loading.value = false;
      }
    );
  }

  function stop() {
    if (unsub) {
      unsub();
      unsub = null;
    }
  }

  const blocksMap = computed(() => {
    const m = new Map();
    blocks.value.forEach((b) => m.set(b.id, b));
    return m;
  });

  function getById(blockId) {
    return blocksMap.value.get(blockId) || null;
  }

  async function createBlock(payload) {
    if (!db) throw new Error('Firestore not ready');
    const ref = await addDoc(collection(db, 'blocks'), {
      ...payload,
      active: payload.active !== false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return ref.id;
  }

  async function updateBlock(blockId, patch) {
    if (!db) throw new Error('Firestore not ready');
    await updateDoc(doc(db, 'blocks', blockId), {
      ...patch,
      updatedAt: serverTimestamp()
    });
  }

  async function deleteBlock(blockId) {
    if (!db) throw new Error('Firestore not ready');
    await deleteDoc(doc(db, 'blocks', blockId));
  }

  return {
    blocks,
    blocksMap,
    loading,
    fetchBlocks,
    stop,
    getById,
    createBlock,
    updateBlock,
    deleteBlock
  };
});
